import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { ApolloError } from '@apollo/client/errors';
import { CreativesetShowcaseService } from '@app/shared/creativeset-showcase/state/creativeset-showcase.service';
import { EnvironmentService } from '@app/shared/services/environment.service';
import { ErrorsRedirectionService } from '@app/shared/services/errors-redirection.service';
import {
    CreativesetErrorMessage,
    OneOfCreativeSetProblemDetails,
    OneOfShowcaseProblemDetails,
    ShowcaseErrorMessage
} from '@studio/domain/api/problem-details';
import { ProblemDetails } from '@studio/domain/api/sapi';
import { take } from 'rxjs';
import { AppComponent } from '../../app.component';
import { isHttpNotFoundError, isHttpForbiddenError } from '../error.utils';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Try again or contact our support for help.';
const FORBIDDEN_ERROR_MESSAGE = 'You do not have permissions to access to this set.';

type HttpErrorWithProblemDetails = Omit<HttpErrorResponse, 'error'> & {
    error: OneOfShowcaseProblemDetails;
};

@Component({
    selector: 'error-404',
    templateUrl: 'error-404-page.component.html',
    styleUrls: ['error-404-page.component.scss']
})
export class Error404Component {
    errorMessage = DEFAULT_ERROR_MESSAGE;
    linkToCreativeset?: string;

    constructor(
        private app: AppComponent,
        private creativesetShowcaseService: CreativesetShowcaseService,
        private environmentService: EnvironmentService,
        private errorsRedirectionService: ErrorsRedirectionService
    ) {
        this.app.loaded = true;

        if (this.environmentService.inShowcaseMode) {
            this.creativesetShowcaseService.error$.pipe(take(1)).subscribe(error => {
                if (error instanceof HttpErrorResponse) {
                    this.handleShowcaseRestError(error);
                } else if (error instanceof ApolloError) {
                    this.handleApolloError(error);
                }
            });
        } else {
            const error = this.errorsRedirectionService.error;
            if (error instanceof HttpErrorResponse) {
                this.handleRESTError(error);
            } else if (error instanceof ApolloError) {
                this.handleApolloError(error);
            }
        }
    }

    private handleRESTError(error: HttpErrorResponse): void {
        if (isHttpNotFoundError(error)) {
            this.errorMessage = this.getCreativesetNotFoundError(error);
        } else if (isHttpForbiddenError(error)) {
            this.errorMessage = FORBIDDEN_ERROR_MESSAGE;
        } else {
            this.errorMessage = DEFAULT_ERROR_MESSAGE;
        }
    }

    private handleApolloError(error: ApolloError): void {
        const networkError = error.networkError;

        if (!this.isHandledApolloNetworkError(networkError)) {
            return;
        }

        this.errorMessage = networkError.error.errors[0].message;

        if (networkError.status !== 400) {
            return;
        }

        const { brandId, creativeSetId } = networkError.error.errors[0]?.extensions?.data || {};

        if (brandId && creativeSetId) {
            this.linkToCreativeset = `/brand/${brandId}/creativeset/${creativeSetId}`;
        }
    }

    private handleShowcaseRestError(errorResponse: HttpErrorResponse): void {
        if (!this.isValidShowcaseError(errorResponse)) {
            return;
        }

        const errorType = errorResponse.error.errorType;

        this.errorMessage = ShowcaseErrorMessage[errorType] ?? ShowcaseErrorMessage.showcaseNotFound;

        switch (errorType) {
            case 'showcaseDisabled':
            case 'showcaseExpired': {
                const { brandId, creativeSetId } = errorResponse.error;
                this.linkToCreativeset = `/brand/${brandId}/creativeset/${creativeSetId}`;
                break;
            }
        }
    }

    private isValidShowcaseError(
        errorResponse: HttpErrorResponse
    ): errorResponse is HttpErrorWithProblemDetails {
        const error = errorResponse.error;
        return this.isShowcaseProblemDetail(error) && this.isValidShowcaseStatus(error.status);
    }

    private isValidShowcaseStatus(status: number): status is 400 | 404 {
        return status === 400 || status === 404;
    }

    private isShowcaseProblemDetail(error: unknown): error is OneOfShowcaseProblemDetails {
        if (!this.isProblemDetailError(error)) {
            return false;
        }

        return !!Object.keys(ShowcaseErrorMessage).includes(error.errorType);
    }

    private isCreativesetProblemDetail(error: unknown): error is OneOfCreativeSetProblemDetails {
        if (!this.isProblemDetailError(error)) {
            return false;
        }

        return !!Object.keys(CreativesetErrorMessage).includes(error.errorType);
    }

    private isProblemDetailError(error: unknown): error is ProblemDetails {
        if (
            !error ||
            typeof error !== 'object' ||
            !('errorType' in error) ||
            typeof error.errorType !== 'string'
        ) {
            return false;
        }

        return true;
    }

    private getCreativesetNotFoundError(errorResponse: HttpErrorResponse): string {
        if (!this.isCreativesetProblemDetail(errorResponse.error)) {
            return CreativesetErrorMessage.creativeSetNotFound;
        }

        const errorType = errorResponse.error.errorType;

        return CreativesetErrorMessage[errorType];
    }

    private isHandledApolloNetworkError(
        networkError: ApolloError['networkError']
    ): networkError is HttpErrorResponse {
        if (!(networkError instanceof HttpErrorResponse)) {
            return false;
        }

        return networkError.status === 404 || networkError.status === 400;
    }
}
